<script lang="ts">
  import { fade } from "svelte/transition";
  import { page } from "$app/stores";
  import optimizeImage, { aspectRatio } from "$lib/utils/optimizeImage";
  import Button from "$lib/atoms/Button.svelte";
  import { beforeNavigate } from "$app/navigation";
  import { menuIsOpen } from "$stores";
  import type { Link } from "$types/data";
  import type { SubnavStoryblok } from "$types/components";

  export let pages: {
    subnav?: SubnavStoryblok[];
    link: Link;
    heading: string;
  }[];
  export let offsetTop = 0;

  let activeSubnav: SubnavStoryblok | undefined;

  // Check if this is a Consumer or Business page
  $: isBusinessPage = $page.url.pathname.startsWith("/business");
  $: isConsumerPage = !isBusinessPage;

  // Check if this is the Check Availability page
  $: isCheckAvailabilityPage = $page.url.pathname.startsWith(
    "/check-availability",
  );

  beforeNavigate(() => {
    activeSubnav = undefined;
    $menuIsOpen = false;
  });
</script>

<div
  class="menu-drawer relative overflow-y-auto lg:hidden px-[20px] md:px-[40px]"
  class:flex={$menuIsOpen}
  class:hidden={!$menuIsOpen}
  style={`
    top: calc(var(--main-top-padding) + ${offsetTop}px);
    height: calc(100% - var(--main-top-padding) - ${offsetTop}px);
  `}
>
  <ul class="menu">
    {#each pages as pageItem}
      {#if !activeSubnav}
        <li
          in:fade={{ duration: 100 }}
          class="menu-item"
          tabindex="-1"
          role="menuitem"
          on:keydown={(e) => {
            if ((pageItem.subnav?.length ?? 0) > 0) {
              activeSubnav = pageItem.subnav?.[0];
              e.preventDefault();
            }
          }}
          on:click={(e) => {
            if ((pageItem.subnav?.length ?? 0) > 0) {
              activeSubnav = pageItem.subnav?.[0];
              e.preventDefault();
            }
          }}
        >
          <a
            href={(pageItem?.subnav?.length ?? 0) > 0
              ? "#"
              : "/" + pageItem.link.cached_url}
            class="type:h3 !text-[#242121] {$page.url.pathname ===
            '/' + (pageItem.link.cached_url || '#')
              ? 'is-active'
              : ''}"
          >
            <span>{pageItem.heading}</span>
            {#if (pageItem.subnav?.length ?? 0) > 0}
              <svg class="arrow w-[25px] h-[25px]"
                ><use xlink:href="/svg/icons.svg#arrow-right-l" /></svg
              >
            {/if}
          </a>
        </li>
      {/if}
      {#if activeSubnav && activeSubnav === pageItem.subnav?.[0]}
        <div in:fade class="inset-0 bg-[rgb(var(--color-light-grey))] z-10">
          <div
            class="flex items-center gap-x-5"
            tabindex="-1"
            role="menuitem"
            on:click={() => (activeSubnav = undefined)}
            on:keydown={() => (activeSubnav = undefined)}
          >
            <div class="w-6 h-6 flex items-center justify-center">
              <svg class="w-[25px] h-[25px] text-charcoal"
                ><use xlink:href="/svg/icons.svg#arrow-left-l" /></svg
              >
            </div>
            {#if activeSubnav?.textCard && activeSubnav.textCard.length > 0}
              <h2 class="type:h3">{activeSubnav.textCard[0].heading}</h2>
            {/if}
          </div>

          {#if activeSubnav?.items && activeSubnav.items.length > 0}
            <ul class="menu sub-menu relative">
              {#each activeSubnav.items as item}
                <li class="menu-item">
                  <a
                    href={"/" + item.link?.cached_url}
                    class="type:h6 !text-[#242121] {$page.url.pathname ===
                    '/' + item.link?.cached_url
                      ? 'is-active'
                      : ''}"
                    on:click={() => {
                      menuIsOpen.set(false);
                    }}
                  >
                    <div
                      class={`
                        flex
                        items-center
                        w-[30px]
                        h-[30px]
                        justify-center
                        bg-sand
                        rounded-full
                      `}
                    >
                      {#if item.icon}
                        <img
                          src={optimizeImage(item.icon.filename)}
                          alt={item.icon?.alt}
                          class="w-5"
                          style={aspectRatio(item.icon.filename)}
                          loading="lazy"
                        />
                      {/if}
                    </div>
                    <span>{item.heading}</span>
                  </a>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
      {/if}
    {/each}
  </ul>

  <div class="contact-cta-wrapper">
    {#if isConsumerPage && !isCheckAvailabilityPage}
      <Button href="/check-availability" color="yellow">Get delivery</Button>
    {/if}

    {#if isBusinessPage}
      <Button href="/business/contact" color="yellow">Contact sales</Button>
    {/if}
  </div>
</div>

<style lang="scss">
  .menu-drawer {
    position: fixed;
    height: calc(100% - var(--main-top-padding));
    z-index: 49;
    top: var(--main-top-padding);
    left: 0;
    width: 100%;
    background: rgb(var(--color-light-grey));
    padding-top: 22px;
    padding-bottom: 22px;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;

    :global(.contact-cta-wrapper) {
      width: 100%;
      flex: 0 0 auto;
    }

    .contact-cta-wrapper :global(.button) {
      width: 100%;
      margin-top: 1em;
    }
  }

  .menu {
    width: 100%;

    &.sub-menu {
      border-top: 1px solid var(--color-stroke-dark-rgba);
      padding-top: 25px;
      margin-top: 25px;
    }
  }

  .menu-item {
    width: 100%;
    margin-top: 25px;

    &:first-child {
      margin-top: 0;
    }

    a {
      text-decoration: none;
      display: flex;
      align-items: center;
      column-gap: 20px;
      color: currentColor;
      padding: 0 0 25px;
      border-bottom: 1px solid var(--color-stroke-dark-rgba);

      .arrow {
        margin-left: auto;
      }

      &.is-active {
        color: rgb(var(--color-text-dark));
      }
    }
  }
</style>
